import { Avatar, Button, Flex, Text, Tooltip } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { AdminMeetingListViewQuery } from '@/@generated/codegen/API';
import CaseClusterTag from '@/components/CaseClusterTag/CaseClusterTag';
import ClientTag from '@/components/ClientTag/ClientTag';
import LiveMeetingInteractionsMenu from '@/components/LiveMeetingInteractionsMenu/LiveMeetingInteractionsMenu';
import { MeetingStatusTag } from '@/components/MeetingStatusTag/MeetingStatusTag';
import { MEETING_STATUS } from '@/features/meeting/helpers/getMeetingStatus';
import dateInRangeFilterFn from '@/ui/TanstackTable/Filter/date/dateInRangeFilterFn';
import { formatMinutesToTime } from '@/utils/helpers/formatMinutesToTime';
import { parseDate } from '@/utils/helpers/parseDate';

import AdminManageMeetingMenu from './AdminManageMeetingMenu/AdminManageMeetingMenu';

export type AdminMeetingListItemType =
  AdminMeetingListViewQuery['listMeetings']['edges'][number]['node'] & {
    meetingStatus: MEETING_STATUS;
    meetingStatusText: string;
    meetingDurationMinutes: number;
  };

const columnHelper = createColumnHelper<AdminMeetingListItemType>();

type Props = {
  nonDeletableMeetings: string[];
  onDupeClick: (meeting: AdminMeetingListItemType) => void;
  onEditClick: (meeting: AdminMeetingListItemType) => void;
  onUserAccess: (meeting: AdminMeetingListItemType) => void;
  onAccessLinkClick: (meeting: AdminMeetingListItemType) => void;
};

const getColumns = ({
  nonDeletableMeetings,
  onDupeClick,
  onEditClick,
  onUserAccess,
  onAccessLinkClick,
}: Props) => {
  const columns = [
    columnHelper.accessor('eventDate', {
      header: 'Event Date',
      filterFn: dateInRangeFilterFn,
      cell: (props) => <Text>{parseDate(props.getValue().toString() || '')}</Text>,
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (props) => (
        <Flex direction="column" gap={2}>
          <Tooltip label={props.getValue()}>
            <Text fontSize="lg">{props.getValue()}</Text>
          </Tooltip>
          <Flex w="full" justifyContent="left" gap={2}>
            <AdminManageMeetingMenu
              meeting={props.row.original}
              nonDeletableMeetings={nonDeletableMeetings}
              onDupeMeeting={onDupeClick}
              onEditMeeting={onEditClick}
              onUserAccess={onUserAccess}
            />
            <LiveMeetingInteractionsMenu
              meeting={props.row.original}
              hostFields={props.row.original.hostFields}
              buttonProps={{
                variant: 'primary',
                borderRadius: 4,
              }}
            />
          </Flex>
        </Flex>
      ),
    }),
    columnHelper.display({
      id: 'participants',
      header: '# participants (max)',
      cell: (props) => {
        const value = props.row.original.userMeetingJoins.edges.length || 0;
        if (!value) return '--';
        return (
          <Flex alignItems="center" gap={2}>
            <Text>{value}</Text>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('clients', {
      id: 'clients',
      enableSorting: false,
      header: 'Client',
      // filterFn: createArrayFilterFn<Client>('clientName'),
      cell: (props) => {
        const clientList = props.row.original.clients.edges.map((el) => el.node);
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {clientList.map((elem) => (
              <ClientTag key={elem.id} client={elem.name} />
            ))}
          </Flex>
        );
      },
    }),
    columnHelper.accessor('meetingStatusText', {
      header: 'Status',
      enableSorting: false,
      cell: (props) => <MeetingStatusTag meeting={props.row.original} />,
      minSize: 190,
    }),

    columnHelper.accessor('caseCluster', {
      id: 'caseCluster',
      enableSorting: false,
      header: 'Case Cluster',
      cell: (props) => <CaseClusterTag name={props.getValue().name} />,
    }),

    columnHelper.accessor('id', {
      header: 'ID',
      cell: (props) => <Text>{props.getValue()}</Text>,
    }),

    columnHelper.accessor('meetingDurationMinutes', {
      enableSorting: false,
      header: 'Duration',
      cell: (props) => {
        const value = props.getValue();

        if (value === 0) return '--';
        if (value < 0) return 'N/A';

        return (
          <Flex alignItems="center" gap={2}>
            <Text>{formatMinutesToTime(value)}</Text>
          </Flex>
        );
      },
      minSize: 100,
    }),

    columnHelper.accessor('host', {
      id: 'host',
      enableSorting: false,
      header: 'Host',
      cell: (props) => (
        <Flex gap={2}>
          <Avatar name={props.getValue().email} color="white" size="xs" />
          <Text size="xs">{props.getValue().email}</Text>
        </Flex>
      ),
    }),

    columnHelper.accessor('teams', {
      id: 'teams',
      enableSorting: false,
      header: 'Teams',
      // filterFn: createArrayFilterFn<Client>('clientName'),
      cell: (props) => {
        const teamList = props.row.original.teams.edges.map((el) => el.node);
        return (
          <Flex flexDir="row" gap={2} flexWrap="wrap">
            {teamList.map((elem) => (
              <ClientTag key={elem.id} client={elem.name} />
            ))}
          </Flex>
        );
      },
    }),

    columnHelper.accessor('eventLocation', {
      header: 'Event Location',
    }),
    columnHelper.display({
      id: 'access links',
      header: 'Access links',
      cell: (props) => {
        return (
          <Button
            onClick={() => {
              onAccessLinkClick(props.row.original);
            }}
            size="sm"
          >
            Manage links
          </Button>
        );
      },
    }),
    columnHelper.accessor('purchaseOrderNbr', {
      header: 'PO',
    }),
    columnHelper.accessor('meetingType', {
      header: 'Type',
    }),
    columnHelper.accessor('isInTesting', {
      header: 'Sandbox',
      cell: (props) => <Text>{props.getValue() ? 'Yes' : 'No'}</Text>,
    }),
    columnHelper.accessor('isArchived', {
      header: 'Archived',
      cell: (props) => <Text>{props.getValue() ? 'Yes' : 'No'}</Text>,
    }),
    columnHelper.accessor('isDemo', {
      header: 'Demo',
      cell: (props) => <Text>{props.getValue() ? 'Yes' : 'No'}</Text>,
    }),
    // columnHelper.display({
    //   id: 'actions',
    //   header: 'Actions',
    //   cell: (props) => (
    //     <Flex justifyContent="left">
    //       <AdminManageMeetingMenu
    //         meeting={props.row.original}
    //         nonDeletableMeetings={nonDeletableMeetings}
    //         onDupeMeeting={onDupeClick}
    //         onEditMeeting={onEditClick}
    //         onUserAccess={onUserAccess}
    //       />
    //       <LiveMeetingInteractionsMenu
    //         meeting={props.row.original}
    //         buttonProps={{
    //           variant: 'primary',
    //           borderRadius: 4,
    //         }}
    //       />
    //     </Flex>
    //   ),
    //   minSize: 200,
    // }),
  ];

  return columns;
};

export default getColumns;
